import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { OrderMeal, OrderMealRequest, UpsellItem, BasicOrderMealItem, MenuItemLookupResult, OrderWithContext } from '@/models';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';
import { AddToBagEvent, ViewBagEvent } from '@/services/analytics/events';
import { AnalyticsMeal, AnalyticsMealType, AnalyticsUpsellType, ProductFindingMethod } from '@/services/analytics/platforms/adobe-analytics/models';
import { PreHideStyleID } from '@/services/adobe-target/adobe-target.service';
import { Product } from '@/services/analytics/platforms/adobe-analytics/models';
import { isEmpty, xor } from 'lodash';
import { TargetDecisionScope } from '@/models/adobe-target';

const UPSELL_MEAL_NAME = 'Added';

@Component({
  name: 'CmgUpsell',
})
export default class CmgUpsell extends Vue {
  @Prop({ type: Boolean })
  public isOrderEligibleForContextualRewards?: boolean;

  loadingIndex = -1;
  hasScrolledIntoView = false;
  itemsAvailableForDisplay: UpsellItem[] = [];

  get menuItemLookupMap(): Map<string, MenuItemLookupResult> | null {
    return this.$appStore.state.menu.menuItemLookupMap;
  }
  
  get displayedUpsellItems(): UpsellItem[] {
    return this.$appStore.state.adobeTarget.displayedUpsellItems;
  }

  get personalizedUpsellTitle(): string {
    return this.$appStore.state.adobeTarget.personalizedUpsellTitle || 'COMPLETE YOUR MEAL';
  }

  get order(): OrderWithContext | null {
    return this.$store.getters.currentOrder;
  }

  get meals(): OrderMeal[] {
    return (this.order && this.order.order.meals) || [];
  }
  
  get validAemUpsellItems(): UpsellItem[] {
    const menuItems = this.menuItemLookupMap;
    if (!menuItems) {
      return [];
    }
    return this.$services.config.getUpsellItems().filter((item: any) => {
      const menuItem = menuItems.get(item.cmgId);
      return menuItem?.isItemAvailable && menuItem.unitPrice && !this.isItemSelectedForUpsell(item.cmgId);
    });
  }

  mounted() {
    this.initializeUpsellImpressionTracking();
  }

  destroyed() {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  async onClickUpsellItem(item: UpsellItem, index: number) {
    // prevent clicking while network request is ongoing
    if (this.loadingIndex != -1) {
      return;
    }

    this.$emit('is-loading', true);
    this.loadingIndex = index;
    const upsellMeal = this.$appStore.state.order.upsellMeal;
    let order = this.order;

    if (!order) {
      this.$services.logger.unreachable('Cannot add an upsell item to null order');
      return;
    }

    const mealItem: BasicOrderMealItem = {
      menuItemId: item.cmgId,
      menuItemName: item.name,
      quantity: 1,
      isUpSell: true,
    };

    try {
      let mealId: string | null = null;
      let removingUpsellItem = this.isItemSelectedForUpsell(item.cmgId);
      if (upsellMeal) {
        let mealRequest: OrderMealRequest;
        if (removingUpsellItem) {
          mealRequest = this.createMealRequestWithoutUpsellItem(mealItem.menuItemId, upsellMeal);
        } else {
          mealRequest = this.createMealRequestWithExistingUpsell(mealItem, upsellMeal);
        }
        if (!mealRequest.meal.sides && !mealRequest.meal.drinks) {
          await this.$services.order.removeMealFromOrder(order, upsellMeal.mealId);
        } else {
          await this.$services.order.updateMealInOrder(order, upsellMeal.mealId, mealRequest);
          mealId = upsellMeal.mealId;
        }

        if (this.isOrderEligibleForContextualRewards) {
          await this.$services.reward.getBagOffers();
        }
      } else {
        const mealRequest: OrderMealRequest = {
          meal: {
            mealName: UPSELL_MEAL_NAME,
            entrees: null,
            sides: this.isSideOrDrink(item.cmgId, 'sides') ? [mealItem] : null,
            drinks: this.isSideOrDrink(item.cmgId, 'drinks') ? [mealItem] : null,
          },
        };
        mealId = await this.$services.order.addMealToOrder(order, mealRequest);

        if (this.isOrderEligibleForContextualRewards) {
          await this.$services.reward.getBagOffers();
        }
      }

      const updatedUpsellMeal = mealId ? this.findMeal(mealId) : null;
-     this.$services.order.setUpsellMeal(updatedUpsellMeal);

      if (!removingUpsellItem) {
        this.sendAdobeUpsellItemAddToBagEvent(updatedUpsellMeal, item)
      }
      
    } finally {
      this.loadingIndex = -1;
      this.$emit('is-loading', false);
    }
  }

  isItemSelectedForUpsell(menuItemId: string): boolean {
    const upsellMeal = this.$appStore.state.order.upsellMeal;
    if (!upsellMeal) {
      return false;
    }

    const foundItem =
      (upsellMeal.sides && upsellMeal.sides.find(side => side.menuItemId == menuItemId)) ||
      (upsellMeal.drinks && upsellMeal.drinks.find(side => side.menuItemId == menuItemId));
    return !!foundItem;
  }

  private isSideOrDrink(menuItemId: string, sideOrDrink: 'sides' | 'drinks') {
    const menu = this.$appStore.state.menu.activeMenu;
    if (!menu || !menu[sideOrDrink]) {
      return false;
    }

    return !!menu[sideOrDrink]!.find(x => x.itemId === menuItemId);
  }

  private findMeal(mealId: string): OrderMeal | null {
    const order = this.$services.order.getCurrentOrder();
    if (!order || !order.order.meals) {
      return null;
    }

    return order.order.meals.find(m => m.mealId === mealId) || null;
  }

  private createMealRequestWithExistingUpsell(mealItem: BasicOrderMealItem, upsellMeal: OrderMeal): OrderMealRequest {
    const mealRequest: OrderMealRequest = {
      meal: {
        mealName: UPSELL_MEAL_NAME,
        entrees: null,
        sides: this.isSideOrDrink(mealItem.menuItemId, 'sides') ? [mealItem] : null,
        drinks: this.isSideOrDrink(mealItem.menuItemId, 'drinks') ? [mealItem] : null,
      },
    };

    if (upsellMeal.sides && upsellMeal.sides.length) {
      if (!mealRequest.meal.sides) {
        mealRequest.meal.sides = [];
      }
      mealRequest.meal.sides.push(...upsellMeal.sides);
    }

    if (upsellMeal.drinks && upsellMeal.drinks.length) {
      if (!mealRequest.meal.drinks) {
        mealRequest.meal.drinks = [];
      }
      mealRequest.meal.drinks.push(...upsellMeal.drinks);
    }

    return mealRequest;
  }

  private createMealRequestWithoutUpsellItem(menuItemId: string, upsellMeal: OrderMeal): OrderMealRequest {
    const sides = upsellMeal.sides ? upsellMeal.sides.filter(side => side.menuItemId !== menuItemId) : null;
    const drinks = upsellMeal.drinks ? upsellMeal.drinks.filter(drink => drink.menuItemId !== menuItemId) : null;

    return {
      meal: {
        mealName: UPSELL_MEAL_NAME,
        entrees: null,
        sides: sides && sides.length == 0 ? null : sides,
        drinks: drinks && drinks.length == 0 ? null : drinks,
      },
    };
  }

  sendAdobeUpsellItemAddToBagEvent(upsellMeal: AnalyticsMeal | null, selectedUpsellItem: UpsellItem) {
    if (!upsellMeal) {
      return;
    }

    // Only send the selected upsell item to Adobe Analytics when adding to bag
    const upsellMealCopy = { ...upsellMeal } as OrderMeal;
    upsellMealCopy.sides = upsellMealCopy?.sides?.filter(side => side.menuItemId == selectedUpsellItem.cmgId) || null;
    upsellMealCopy.drinks = upsellMealCopy?.drinks?.filter(side => side.menuItemId == selectedUpsellItem.cmgId) || null;
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(upsellMealCopy, AnalyticsMealType.Standard, false, ProductFindingMethod.Upsell), this.$appStore, this.$services));
  }

  private isUpsellSectionVisible(element: Element): boolean {
    if (!element) {
      return false;
    }

    const boundingRect = element.getBoundingClientRect();
    return !(boundingRect.top + 170 > window.innerHeight || boundingRect.bottom - 270  < 0);
  }

  private updateUpsellItems(): void {
    this.$services.adobeTarget.prehideContent(PreHideStyleID.upsellItems, 300);

    const previousUpsellItemIds = this.displayedUpsellItems.map(item => item.cmgId);
    const personalizedUpsellitemIds = this.$services.adobeTarget.getPersonalizedUpsellItems();
    const validAemUpsellItems = this.validAemUpsellItems;

    // Maintain order of any selected upsell items. If item is
    // not selected update with personalized or default item
    if (previousUpsellItemIds.length > 0) {
      previousUpsellItemIds.forEach((cmgId, index) => {
        if (this.isItemSelectedForUpsell(cmgId)) {
          this.itemsAvailableForDisplay.push(this.displayedUpsellItems[index]);
        } else  {
          const personalizedItem  = this.getNextAvailablePersonalizedItem(personalizedUpsellitemIds, validAemUpsellItems);

          if (personalizedItem) {
            this.addItem(personalizedItem, AnalyticsUpsellType.Personal);
          } else {
            this.addItem(validAemUpsellItems.shift(), AnalyticsUpsellType.Static);
          }
        }
      });

    } else {
      this.addPersonalizedItems(personalizedUpsellitemIds, validAemUpsellItems);
    }

    this.addDefaultUpsellItems(validAemUpsellItems);

    this.$services.adobeTarget.setDisplayedUpsellItems(this.itemsAvailableForDisplay.slice(0,3));
    
    this.setUpsellImpressionHasChanged(previousUpsellItemIds);
  }

  private getNextAvailablePersonalizedItem(personalizedUpsellitems: string[], validAemUpsellItems: UpsellItem[]): UpsellItem | undefined {
    while(personalizedUpsellitems.length) {
      let personalizedItemId = personalizedUpsellitems.shift();
      if (!this.itemsAvailableForDisplay.find(item => item.cmgId == personalizedItemId)) {
        const itemIndex = validAemUpsellItems?.findIndex(fdi => fdi.cmgId == personalizedItemId);
        if (itemIndex >= 0) {
          const menuItem = validAemUpsellItems.splice(itemIndex, 1)[0];
          if (menuItem) {
            return menuItem;
          }
        }
      } else {
        continue;
      }
    }
  }

  private addPersonalizedItems(personalizedUpsellitems: string[], dynamicUpsellItems: UpsellItem[]): void {
    while(personalizedUpsellitems.length) {
      const personalizedItemId = personalizedUpsellitems.shift();
      const menuItem = dynamicUpsellItems.find(dui => dui.cmgId == personalizedItemId);
      if (menuItem) {
       this.addItem(menuItem, AnalyticsUpsellType.Personal)
      } 
    }
  }

  private addItem(item: UpsellItem | undefined, upsellType: AnalyticsUpsellType): void {
    if (!item) {
      return;
    }

    const menuItem = this.menuItemLookupMap?.get(item.cmgId);
    if (!menuItem || this.itemsAvailableForDisplay.find(availableItem => item?.cmgId == availableItem.cmgId)) {
      return;
    }

    item.name = menuItem.menuItemName;
    item.price = this.$services.utils.formatPrice(this.$services.menu.getItemPrice(menuItem));
    item.upsellType = upsellType;
    this.itemsAvailableForDisplay.push(item);
  }

  private addDefaultUpsellItems(defaultUpsellItems: UpsellItem[]): void {
    for (const item of defaultUpsellItems) {
      this.addItem(item, AnalyticsUpsellType.Static);
    }
  }

  private setUpsellImpressionHasChanged(previousUpsellItems: string[]): void {
    const currentUpsellItems = this.$appStore.state.adobeTarget.displayedUpsellItems.map(item => item.cmgId);
    this.$services.analytics.setHasUpsellImpressionChanged(!isEmpty(xor(previousUpsellItems, currentUpsellItems)));
  }

  private onScroll() {
    if (this.isUpsellSectionVisible(this.$refs.upsellSection as Element)) {
      if (!this.hasScrolledIntoView && this.$appStore.state.adobe.hasUpsellImpressionChanged) {
        this.$services.analytics.send(new ViewBagEvent(this.meals, this.createProductListForUpsellItems()));
      }
      this.hasScrolledIntoView = true;
    }
  }

  private initializeUpsellImpressionTracking(): void {
    this.$services.adobeTarget.sendStoredPropositionDisplayEvent([TargetDecisionScope.CartUpsellItems, TargetDecisionScope.CartUpsellTitle]);
    this.updateUpsellItems();
    setTimeout(() => {
      if(this.isUpsellSectionVisible(this.$refs.upsellSection as Element) && !this.hasScrolledIntoView && this.$appStore.state.adobe.hasUpsellImpressionChanged) {
        this.$services.analytics.setUpsellImpressionToStore(this.createProductListForUpsellItems());
        this.hasScrolledIntoView = true;
      }
      window.addEventListener('scroll', this.onScroll, true);
    }, 300);
  }

  private createProductListForUpsellItems(): Product[] {
    const upsellProductListItems:Product[] = []

    this.displayedUpsellItems.forEach(ui => {
      if (!this.isItemSelectedForUpsell(ui.cmgId)) {
        const menuItem = this.menuItemLookupMap?.get(ui.cmgId);
        const product: Product = {
          SKU: ui.cmgId,
          name: ui.shortName,
          priceTotal: this.$services.order.isDelivery() ? menuItem?.unitDeliveryPrice : menuItem?.unitPrice,
          quantity: 1,
          _merchVars: {
            upsellType: ui.upsellType || AnalyticsUpsellType.Static
          }
        };
        upsellProductListItems.push(product);
      }
    });

    return upsellProductListItems;
  }
}
